import { useRouter } from '#imports'
import type {
  RouteLocationNormalized,
  RouteLocationRaw,
  RouteLocationResolved,
} from 'vue-router'

import { isEmpty } from '@backmarket/utils/object/isEmpty'

import {
  PERMISSION_TYPES,
  SCOPES,
  type ScopePermissionType,
} from '~/constants/permissions'
import { useSellerInfoStore } from '~/scopes/auth/stores/sellerInfoStore'

function findScopeRelatedToRoute(route: RouteLocationResolved) {
  return Object.values(SCOPES).find(({ routeNames }) => {
    return (
      routeNames.includes(route.name as string) ||
      route.matched?.some((record) =>
        routeNames.includes(record.name as string),
      )
    )
  })
}

export function usePermissions() {
  // Be careful, permissions is empty for every Pastrami routes
  const { isAdmin, permissions = [] } = useSellerInfoStore()
  const router = useRouter()

  function hasScopeAccess(
    scopeName: string,
    permissionType: ScopePermissionType = PERMISSION_TYPES.EDIT,
  ) {
    // Currently, there is only one permission for read and write: EDIT.
    // The backend format to determine a permission is: SCOPE_NAME.PERMISSION_TYPE (ex: home.edit)
    // By computing the scope name that matched the current route with the edit permission, we can determine if the user has access.
    return isAdmin || permissions.includes(`${scopeName}.${permissionType}`)
  }

  function hasRouteAccess(
    route: RouteLocationNormalized | RouteLocationRaw,
    permissionType: ScopePermissionType = PERMISSION_TYPES.EDIT,
  ) {
    const resolvedRoute = router.resolve(route)
    const scope = findScopeRelatedToRoute(resolvedRoute)
    // this mean that the page we are trying to access does not require any permissions
    if (isEmpty(scope)) return true

    return hasScopeAccess(scope.name, permissionType)
  }

  return {
    hasRouteAccess,
    hasScopeAccess,
  }
}
